import React, { createContext, useContext, useState, useEffect, useRef, useCallback, ReactNode } from 'react'
import { io, Socket } from 'socket.io-client'
import { useIsAuthenticated, useUser } from '@/app/store/auth'

interface PresenceContextType {
    onlineUsers: Set<number>
    isUserOnline: (userId: number) => boolean
    socket: Socket | null
}

const PresenceContext = createContext<PresenceContextType>({
    onlineUsers: new Set(),
    isUserOnline: () => false,
    socket: null,
})

export function PresenceProvider({ children }: { children: ReactNode }) {
    const isAuthenticated = useIsAuthenticated()
    const user = useUser()
    const [onlineUsers, setOnlineUsers] = useState<Set<number>>(new Set())
    const socketRef = useRef<Socket | null>(null)

    useEffect(() => {
        if (!isAuthenticated || !user?.id) {
            // Disconnect if logged out
            if (socketRef.current) {
                socketRef.current.disconnect()
                socketRef.current = null
            }
            setOnlineUsers(new Set())
            return
        }

        // Connect to the default namespace for presence
        // In dev: Vite proxies /socket.io to backend, so use same origin
        // In production: VITE_API_BASE_URL points to the backend (ALB/API Gateway)
        const apiBase = import.meta.env.VITE_API_BASE_URL
        const socketBaseUrl = apiBase || window.location.origin

        const socket = io(socketBaseUrl, {
            path: '/socket.io',
            transports: ['websocket', 'polling'],
            reconnection: true,
            reconnectionDelay: 2000,
            reconnectionDelayMax: 10000,
            reconnectionAttempts: 5,
        })

        socketRef.current = socket

        socket.on('connect', () => {
            socket.emit('identify-user', { userId: user.id })
            socket.emit('request-online-users')
        })

        socket.on('online-users-list', (data: { userIds: number[] }) => {
            setOnlineUsers(new Set(data.userIds || []))
        })

        socket.on('user-online', (data: { userId: number }) => {
            setOnlineUsers(prev => new Set([...prev, data.userId]))
        })

        socket.on('user-offline', (data: { userId: number }) => {
            setOnlineUsers(prev => {
                const next = new Set(prev)
                next.delete(data.userId)
                return next
            })
        })

        return () => {
            socket.disconnect()
            socketRef.current = null
        }
    }, [isAuthenticated, user?.id])

    const isUserOnline = useCallback((userId: number) => {
        return onlineUsers.has(userId)
    }, [onlineUsers])

    return (
        <PresenceContext.Provider value={{ onlineUsers, isUserOnline, socket: socketRef.current }}>
            {children}
        </PresenceContext.Provider>
    )
}

export function usePresence() {
    return useContext(PresenceContext)
}

export function useIsOnline(userId: number | null | undefined) {
    const { isUserOnline } = usePresence()
    if (!userId) return false
    return isUserOnline(userId)
}
